<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import FluentIcon from 'shared/components/FluentIcon/DashboardIcon.vue';

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const { showAlert } = useAlert();

const records = computed(
  () => store.getters['pipelineSequenceDefinitions/getSequenceDefinitions']
);
const isLoading = computed(
  () =>
    store.getters['pipelineSequenceDefinitions/isFetchingSequenceDefinitions']
);

const filterStatus = ref('all'); // all|active|inactive
const searchTerm = ref('');

const filteredRecords = computed(() => {
  let filtered = records.value;
  if (filterStatus.value === 'active')
    filtered = filtered.filter(r => r.active);
  else if (filterStatus.value === 'inactive')
    filtered = filtered.filter(r => !r.active);
  if (searchTerm.value.trim()) {
    const q = searchTerm.value.trim().toLowerCase();
    filtered = filtered.filter(
      r =>
        r.name?.toLowerCase().includes(q) ||
        r.description?.toLowerCase().includes(q)
    );
  }
  return filtered;
});

const stats = computed(() => ({
  total: records.value.length,
  active: records.value.filter(r => r.active).length,
  inactive: records.value.filter(r => !r.active).length,
}));

const triggerLabel = type => {
  const map = {
    manual: t('PIPELINE.SEQUENCE_DEFINITIONS.TRIGGER_MANUAL'),
    stage_change: t('PIPELINE.SEQUENCE_DEFINITIONS.TRIGGER_STAGE_CHANGE'),
    time_based: t('PIPELINE.SEQUENCE_DEFINITIONS.TRIGGER_TIME_BASED'),
    condition_based: t('PIPELINE.SEQUENCE_DEFINITIONS.TRIGGER_CONDITION_BASED'),
  };
  return map[type] || t('PIPELINE.SEQUENCE_DEFINITIONS.TRIGGER_MANUAL');
};

const totalDays = steps => {
  if (!Array.isArray(steps)) return 0;
  return Math.round(
    steps.reduce(
      (sum, s) =>
        sum + (Number(s.delay_days) || 0) + (Number(s.delay_hours) || 0) / 24,
      0
    )
  );
};

const goToNew = () => router.push({ name: 'pipeline_sequence_new' });
const goToEdit = id =>
  router.push({ name: 'pipeline_sequence_edit', params: { id } });

const toggleActive = async record => {
  try {
    await store.dispatch('pipelineSequenceDefinitions/toggleActive', {
      id: record.id,
      active: !record.active,
    });
    showAlert(
      record.active
        ? t('PIPELINE.SEQUENCE_DEFINITIONS.DEACTIVATE_SUCCESS')
        : t('PIPELINE.SEQUENCE_DEFINITIONS.ACTIVATE_SUCCESS')
    );
  } catch (e) {
    showAlert(e.message || 'Erro');
  }
};

const duplicate = async record => {
  try {
    const created = await store.dispatch(
      'pipelineSequenceDefinitions/duplicate',
      {
        id: record.id,
      }
    );
    showAlert(t('PIPELINE.SEQUENCE_DEFINITIONS.SAVE_SUCCESS'));
    if (created?.id) goToEdit(created.id);
  } catch (e) {
    showAlert(e.message);
  }
};

const remove = async record => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('PIPELINE.SEQUENCE_DEFINITIONS.DELETE_CONFIRM_BODY')))
    return;
  try {
    await store.dispatch('pipelineSequenceDefinitions/delete', record.id);
    showAlert(t('PIPELINE.SEQUENCE_DEFINITIONS.DELETE_SUCCESS'));
  } catch (e) {
    showAlert(e.response?.data?.error || e.message);
  }
};

// Map de ícones por tipo de atividade (preview no card)
const activityIcon = type => {
  const map = {
    call: 'call',
    email: 'mail',
    meeting: 'people',
    task: 'tasks',
    note: 'note',
    demo: 'video',
    follow_up: 'arrow-clockwise',
  };
  return map[type] || 'circle';
};

onMounted(() => {
  store.dispatch('pipelineSequenceDefinitions/fetch');
});
</script>

<template>
  <div class="flex flex-col flex-1 w-full min-h-0 bg-n-background">
    <!-- Header -->
    <header
      class="px-8 py-5 border-b border-n-weak bg-n-solid-1 flex items-center justify-between flex-shrink-0"
    >
      <div class="flex items-center gap-3">
        <div
          class="w-10 h-10 rounded-xl bg-gradient-to-br from-n-brand to-n-brand/70 flex items-center justify-center shadow-md"
        >
          <FluentIcon icon="book" size="20" class="text-white" />
        </div>
        <div>
          <h1 class="text-lg font-semibold text-n-slate-12 leading-tight">
            {{ t('PIPELINE.SEQUENCE_DEFINITIONS.PAGE_TITLE') }}
          </h1>
          <p class="text-xs text-n-slate-10 mt-0.5">
            {{ t('PIPELINE.SEQUENCE_DEFINITIONS.PAGE_SUBTITLE') }}
          </p>
        </div>
      </div>
      <button
        type="button"
        class="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-n-brand text-white text-sm font-medium hover:bg-n-brand-hover transition-colors shadow-sm"
        @click="goToNew"
      >
        <FluentIcon icon="add" size="16" />
        {{ t('PIPELINE.SEQUENCE_DEFINITIONS.BTN_NEW') }}
      </button>
    </header>

    <!-- Stats + filters bar -->
    <div
      class="px-8 py-4 border-b border-n-weak bg-n-solid-2/50 flex items-center justify-between gap-4 flex-shrink-0 flex-wrap"
    >
      <div class="flex items-center gap-2">
        <button
          v-for="status in [
            {
              id: 'all',
              label: t('PIPELINE.SEQUENCE_DEFINITIONS.STATUS_ALL'),
              count: stats.total,
            },
            {
              id: 'active',
              label: t('PIPELINE.SEQUENCE_DEFINITIONS.STATUS_ACTIVE'),
              count: stats.active,
            },
            {
              id: 'inactive',
              label: t('PIPELINE.SEQUENCE_DEFINITIONS.STATUS_INACTIVE'),
              count: stats.inactive,
            },
          ]"
          :key="status.id"
          type="button"
          class="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all"
          :class="
            filterStatus === status.id
              ? 'bg-n-brand text-white shadow-sm'
              : 'text-n-slate-11 hover:bg-n-alpha-2 bg-transparent'
          "
          @click="filterStatus = status.id"
        >
          {{ status.label }}
          <span
            class="px-1.5 py-0.5 rounded-md text-[10px] font-bold"
            :class="
              filterStatus === status.id
                ? 'bg-white/20 text-white'
                : 'bg-n-alpha-2 text-n-slate-11'
            "
          >
            {{ status.count }}
          </span>
        </button>
      </div>
      <div class="relative flex-1 max-w-sm">
        <FluentIcon
          icon="search"
          size="14"
          class="absolute left-3 top-1/2 -translate-y-1/2 text-n-slate-9 pointer-events-none"
        />
        <input
          v-model="searchTerm"
          type="text"
          :placeholder="t('PIPELINE.SEQUENCE_DEFINITIONS.SEARCH_PLACEHOLDER')"
          class="w-full pl-9 pr-3 py-2 rounded-lg border border-n-weak bg-n-solid-1 text-sm text-n-slate-12 focus:outline-none focus:border-n-brand transition-colors"
        />
      </div>
    </div>

    <!-- Body — grid of cards -->
    <div class="flex-1 overflow-auto px-8 py-6">
      <div v-if="isLoading" class="flex items-center justify-center py-20">
        <span class="loading-spinner-small mr-2" />
        <span class="text-n-slate-10 text-sm">{{
          t('PIPELINE.SEQUENCES.LOADING')
        }}</span>
      </div>

      <div
        v-else-if="!filteredRecords.length && !searchTerm"
        class="flex flex-col items-center justify-center py-20 text-center"
      >
        <div
          class="w-16 h-16 rounded-2xl bg-n-alpha-2 flex items-center justify-center mb-4"
        >
          <FluentIcon icon="book" size="28" class="text-n-slate-9" />
        </div>
        <h3 class="text-base font-semibold text-n-slate-12 mb-2">
          {{ t('PIPELINE.SEQUENCE_DEFINITIONS.EMPTY_TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-10 max-w-md mb-4">
          {{ t('PIPELINE.SEQUENCE_DEFINITIONS.EMPTY_BODY') }}
        </p>
        <button
          type="button"
          class="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-n-brand text-white text-sm font-medium hover:bg-n-brand-hover transition-colors"
          @click="goToNew"
        >
          <FluentIcon icon="add" size="14" />
          {{ t('PIPELINE.SEQUENCE_DEFINITIONS.BTN_NEW') }}
        </button>
      </div>

      <div
        v-else-if="!filteredRecords.length"
        class="text-center py-20 text-sm text-n-slate-10"
      >
        {{
          t('PIPELINE.SEQUENCE_DEFINITIONS.NO_RESULTS', { term: searchTerm })
        }}
      </div>

      <div
        v-else
        class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4"
      >
        <article
          v-for="record in filteredRecords"
          :key="record.id"
          class="group bg-n-solid-1 border border-n-weak rounded-xl p-5 hover:shadow-lg hover:border-n-brand/30 hover:-translate-y-0.5 transition-all cursor-pointer flex flex-col"
          @click="goToEdit(record.id)"
        >
          <!-- Header: Status badge + actions -->
          <div class="flex items-start justify-between mb-3">
            <span
              class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold"
              :class="
                record.active
                  ? 'bg-teal-3/80 text-teal-11 border border-teal-6/30'
                  : 'bg-n-alpha-2 text-n-slate-10 border border-n-weak'
              "
            >
              <span
                class="w-1.5 h-1.5 rounded-full"
                :class="record.active ? 'bg-teal-9' : 'bg-n-slate-9'"
              />
              {{
                record.active
                  ? t('PIPELINE.SEQUENCE_DEFINITIONS.STATUS_ACTIVE')
                  : t('PIPELINE.SEQUENCE_DEFINITIONS.STATUS_INACTIVE')
              }}
            </span>
            <div
              class="inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
              @click.stop
            >
              <button
                v-tooltip="t('PIPELINE.SEQUENCE_DEFINITIONS.BTN_EDIT')"
                type="button"
                class="p-1.5 rounded-md text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
                @click.stop="goToEdit(record.id)"
              >
                <FluentIcon icon="edit" size="14" />
              </button>
              <button
                v-tooltip="t('PIPELINE.SEQUENCE_DEFINITIONS.BTN_DUPLICATE')"
                type="button"
                class="p-1.5 rounded-md text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
                @click.stop="duplicate(record)"
              >
                <FluentIcon icon="copy" size="14" />
              </button>
              <button
                v-tooltip="
                  record.active
                    ? t('PIPELINE.SEQUENCE_DEFINITIONS.BTN_DEACTIVATE')
                    : t('PIPELINE.SEQUENCE_DEFINITIONS.BTN_ACTIVATE')
                "
                type="button"
                class="p-1.5 rounded-md text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
                @click.stop="toggleActive(record)"
              >
                <FluentIcon
                  :icon="record.active ? 'dismiss-circle' : 'play-circle'"
                  size="14"
                />
              </button>
              <button
                v-tooltip="t('PIPELINE.SEQUENCE_DEFINITIONS.BTN_DELETE')"
                type="button"
                class="p-1.5 rounded-md text-ruby-10 hover:text-ruby-11 hover:bg-ruby-3 transition-colors"
                @click.stop="remove(record)"
              >
                <FluentIcon icon="delete" size="14" />
              </button>
            </div>
          </div>

          <!-- Title + description -->
          <h3
            class="text-base font-semibold text-n-slate-12 mb-1 line-clamp-2 leading-snug group-hover:text-n-brand transition-colors"
          >
            {{ record.name }}
          </h3>
          <p
            v-if="record.description"
            class="text-xs text-n-slate-10 line-clamp-2 mb-4 leading-relaxed flex-1"
          >
            {{ record.description }}
          </p>
          <div v-else class="flex-1" />

          <!-- Steps mini-preview -->
          <div v-if="record.steps?.length" class="mb-4">
            <div class="flex items-center gap-1.5 mb-2">
              <FluentIcon icon="circle-line" size="12" class="text-n-slate-9" />
              <span
                class="text-[11px] font-medium text-n-slate-10 uppercase tracking-wide"
              >
                {{ t('PIPELINE.SEQUENCE_DEFINITIONS.FLOW_LABEL') }}
              </span>
            </div>
            <div class="flex items-center gap-1 flex-wrap">
              <template
                v-for="(step, idx) in record.steps.slice(0, 5)"
                :key="idx"
              >
                <span
                  v-tooltip="step.title"
                  class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-n-alpha-1 border border-n-weak/50 text-[10px] text-n-slate-11"
                >
                  <FluentIcon
                    :icon="activityIcon(step.activity_type)"
                    size="10"
                  />
                  {{ idx + 1 }}
                </span>
                <FluentIcon
                  v-if="idx < Math.min(record.steps.length - 1, 4)"
                  icon="chevron-right"
                  size="10"
                  class="text-n-slate-9"
                />
              </template>
              <span
                v-if="record.steps.length > 5"
                class="text-[10px] text-n-slate-9 px-1"
              >
                {{
                  t('PIPELINE.SEQUENCE_DEFINITIONS.MORE_STEPS', {
                    n: record.steps.length - 5,
                  })
                }}
              </span>
            </div>
          </div>

          <!-- Footer stats -->
          <div
            class="grid grid-cols-3 gap-2 pt-3 border-t border-n-weak/50 text-center"
          >
            <div>
              <p class="text-base font-bold text-n-slate-12">
                {{ record.steps?.length || 0 }}
              </p>
              <p class="text-[10px] text-n-slate-10 uppercase tracking-wide">
                {{ t('PIPELINE.SEQUENCE_DEFINITIONS.STATS_STEPS') }}
              </p>
            </div>
            <div>
              <p class="text-base font-bold text-n-slate-12">
                {{
                  t('PIPELINE.SEQUENCE_DEFINITIONS.DAYS_SHORT', {
                    n: totalDays(record.steps),
                  })
                }}
              </p>
              <p class="text-[10px] text-n-slate-10 uppercase tracking-wide">
                {{ t('PIPELINE.SEQUENCE_DEFINITIONS.STATS_DURATION') }}
              </p>
            </div>
            <div>
              <p class="text-xs font-semibold text-n-slate-11 truncate mt-0.5">
                {{ triggerLabel(record.trigger_type) }}
              </p>
              <p class="text-[10px] text-n-slate-10 uppercase tracking-wide">
                {{ t('PIPELINE.SEQUENCE_DEFINITIONS.STATS_TRIGGER') }}
              </p>
            </div>
          </div>
        </article>
      </div>
    </div>
  </div>
</template>

<style scoped>
.loading-spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid var(--color-n-alpha-2, rgba(0, 0, 0, 0.1));
  border-top-color: var(--color-n-brand, #5b6cff);
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  display: inline-block;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
